/* ==========================================================================
   Icon Diamonds — ring builder
   Pairs a setting with a centre stone, keeps the choice across page loads and
   adds both lines to the cart with a shared build id.

   No dependencies. Safe to load with `defer` on every page.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'id_builder_v1';
  var MAX_AGE_MS = 1000 * 60 * 60 * 24 * 14; // a build older than two weeks is stale

  /* ----------------------------------------------------------- storage --- */
  function readStore() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return emptyState();
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return emptyState();
      if (data.updated && Date.now() - data.updated > MAX_AGE_MS) return emptyState();
      return {
        active: !!data.active,
        setting: data.setting || null,
        diamond: data.diamond || null,
        updated: data.updated || Date.now()
      };
    } catch (e) {
      // Private mode, blocked storage, corrupted JSON — the builder still works
      // for the current page, it just does not persist.
      return emptyState();
    }
  }

  function writeStore(state) {
    state.updated = Date.now();
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) { /* non-fatal */ }
    memory = state;
    document.dispatchEvent(new CustomEvent('icon-diamonds:builder:change', { detail: state }));
    return state;
  }

  function emptyState() {
    return { active: false, setting: null, diamond: null, updated: Date.now() };
  }

  var memory = readStore();

  /* ------------------------------------------------------------ config --- */
  function config() {
    var el = document.querySelector('[data-id-builder-config]');
    var d = el ? el.dataset : {};
    return {
      settingsUrl: d.idSettingsUrl || '/collections/nivoda-settings',
      diamondsUrl: d.idDiamondsUrl || '/collections/diamonds',
      reviewUrl: d.idReviewUrl || '/pages/ring-builder',
      cartUrl: d.idCartUrl || '/cart',
      moneyFormat: d.idMoneyFormat || '${{amount}}',
      strings: {
        added: d.idStrAdded || 'Added to cart',
        error: d.idStrError || 'Something went wrong. Please try again.',
        incomplete: d.idStrIncomplete || 'Choose both a setting and a centre stone first.',
        needSize: d.idStrNeedSize || 'Please choose a ring size.',
        notChosen: d.idStrNotChosen || 'Not chosen',
        withStone: d.idStrWithStone || 'With stone:',
        withSetting: d.idStrWithSetting || 'With setting:'
      }
    };
  }

  /* ------------------------------------------------------------- money --- */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
    var name = placeholder ? placeholder[1] : 'amount';
    var out;
    switch (name) {
      case 'amount_no_decimals': out = whole; break;
      case 'amount_with_comma_separator': out = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1]; break;
      case 'amount_no_decimals_with_comma_separator': out = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'); break;
      default: out = whole + '.' + parts[1];
    }
    return (format || '${{amount}}').replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  /* --------------------------------------------------------------- API --- */
  var Builder = {
    get: function () { return memory; },

    start: function () {
      var s = readStore();
      s.active = true;
      return writeStore(s);
    },

    /**
     * @param {'setting'|'diamond'} kind
     * @param {object} item {variantId, productId, title, url, image, price, shape, metal, size}
     */
    select: function (kind, item) {
      if (kind !== 'setting' && kind !== 'diamond') return memory;
      var s = readStore();
      s.active = true;
      s[kind] = item;
      return writeStore(s);
    },

    remove: function (kind) {
      var s = readStore();
      s[kind] = null;
      if (!s.setting && !s.diamond) s.active = false;
      return writeStore(s);
    },

    clear: function () { return writeStore(emptyState()); },

    isComplete: function () {
      var s = memory;
      return !!(s.setting && s.setting.variantId && s.diamond && s.diamond.variantId);
    },

    totalCents: function () {
      var s = memory, t = 0;
      if (s.setting && typeof s.setting.price === 'number') t += s.setting.price;
      if (s.diamond && typeof s.diamond.price === 'number') t += s.diamond.price;
      return t;
    },

    /** Next step the customer still has to complete. */
    nextStep: function () {
      var s = memory;
      if (!s.setting) return 'setting';
      if (!s.diamond) return 'diamond';
      return 'review';
    },

    addToCart: function () {
      var cfg = config();
      if (!Builder.isComplete()) return Promise.reject(new Error(cfg.strings.incomplete));

      var s = memory;
      var buildId = 'ID-' + Date.now().toString(36).toUpperCase();

      var settingProps = { _build_id: buildId, 'Custom ring': 'Setting' };
      if (s.setting.size) settingProps['Ring size'] = s.setting.size;
      if (s.diamond.title) settingProps['Centre stone'] = s.diamond.title;

      var diamondProps = { _build_id: buildId, 'Custom ring': 'Centre stone' };
      if (s.setting.title) diamondProps['Set in'] = s.setting.title;

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: s.setting.variantId, quantity: 1, properties: settingProps },
            { id: s.diamond.variantId, quantity: 1, properties: diamondProps }
          ]
        })
      }).then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.description || body.message || cfg.strings.error);
          Builder.clear();
          return body;
        });
      });
    }
  };

  window.IconDiamondsBuilder = Builder;

  /* ------------------------------------------------------------ the bar --- */
  function renderBar() {
    var bar = document.querySelector('[data-id-builder-bar]');
    if (!bar) return;
    var cfg = config();
    var s = memory;

    bar.setAttribute('data-active', s.active && (s.setting || s.diamond) ? 'true' : 'false');
    if (bar.getAttribute('data-active') !== 'true') { bar.innerHTML = ''; return; }

    var next = Builder.nextStep();
    var nextUrl = next === 'setting' ? cfg.settingsUrl : next === 'diamond' ? cfg.diamondsUrl : cfg.reviewUrl;
    var nextLabel = bar.dataset['idLabel' + next.charAt(0).toUpperCase() + next.slice(1)] ||
      (next === 'setting' ? 'Choose a setting' : next === 'diamond' ? 'Choose a diamond' : 'Review your ring');

    bar.innerHTML =
      '<div class="id-builderbar__slots">' +
        slotHtml('setting', s.setting, bar.dataset.idLabelSettingSlot || 'Setting', cfg) +
        slotHtml('diamond', s.diamond, bar.dataset.idLabelDiamondSlot || 'Centre stone', cfg) +
      '</div>' +
      '<div class="id-builderbar__actions">' +
        (Builder.isComplete()
          ? '<strong>' + formatMoney(Builder.totalCents(), cfg.moneyFormat) + '</strong>'
          : '') +
        '<a class="id-btn id-btn--sm" href="' + escapeAttr(nextUrl) + '">' + escapeHtml(nextLabel) + '</a>' +
        '<button type="button" class="id-linkbtn" data-id-builder-clear>' +
          escapeHtml(bar.dataset.idLabelClear || 'Start over') +
        '</button>' +
      '</div>';
  }

  function slotHtml(kind, item, label, cfg) {
    if (!item) {
      return '<div class="id-builderbar__slot id-builderbar__muted">' +
        '<span class="id-builderbar__thumb" aria-hidden="true">+</span>' +
        '<span>' + escapeHtml(label) + '<br><em>' + escapeHtml(cfg.strings.notChosen) + '</em></span>' +
        '</div>';
    }
    var img = item.image
      ? '<img class="id-builderbar__thumb" src="' + escapeAttr(item.image) + '" alt="" width="40" height="40" loading="lazy">'
      : '<span class="id-builderbar__thumb" aria-hidden="true">&#9679;</span>';
    return '<div class="id-builderbar__slot">' + img +
      '<span><span class="id-builderbar__muted">' + escapeHtml(label) + '</span><br>' +
      '<span class="id-builderbar__name" title="' + escapeAttr(item.title || '') + '">' + escapeHtml(item.title || '') + '</span></span>' +
      '</div>';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  /* ------------------------------------------------- the chevron header --- */
  var TICK = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="10" cy="10" r="8.2"/><path d="m6.4 10.2 2.5 2.5 4.7-5"/></svg>';

  function renderSteps() {
    var bar = document.querySelector('[data-id-builder-steps]');
    if (!bar) return;
    var cfg = config();
    var s = memory;
    var d = bar.dataset;

    var live = s.active && (s.setting || s.diamond);
    bar.setAttribute('data-active', live ? 'true' : 'false');
    if (!live) { bar.innerHTML = ''; return; }

    var next = Builder.nextStep();
    var steps = [
      { n: 1, kind: 'setting', eyebrow: d.idStrSelect, label: d.idStrSetting, item: s.setting, url: cfg.settingsUrl },
      { n: 2, kind: 'diamond', eyebrow: d.idStrSelect, label: d.idStrStone,   item: s.diamond, url: cfg.diamondsUrl },
      { n: 3, kind: 'review',  eyebrow: d.idStrComplete, label: d.idStrRing,  item: null,      url: cfg.reviewUrl }
    ];

    bar.innerHTML = '<div class="id-stepsbar__inner">' + steps.map(function (step) {
      var state = step.kind === 'review'
        ? (Builder.isComplete() ? (next === 'review' ? 'current' : 'done') : '')
        : (step.item ? 'done' : (next === step.kind ? 'current' : ''));

      var aside;
      if (step.kind === 'review') {
        aside = '<span class="id-stepc__aside">' +
          '<span class="id-stepc__eyebrow">' + escapeHtml(d.idStrTotal) + '</span>' +
          '<span class="id-stepc__price">' +
            escapeHtml(Builder.totalCents() ? formatMoney(Builder.totalCents(), cfg.moneyFormat) : '—') +
          '</span></span>';
      } else if (step.item) {
        aside = '<span class="id-stepc__aside">' +
          '<span class="id-stepc__pick" title="' + escapeAttr(step.item.title || '') + '">' +
            escapeHtml(step.item.title || '') + '</span>' +
          '<span class="id-stepc__meta">' +
            '<span class="id-stepc__action">' + escapeHtml(next === step.kind ? d.idStrView : d.idStrChange) + '</span>' +
            '<span class="id-stepc__price">' + escapeHtml(formatMoney(step.item.price, cfg.moneyFormat)) + '</span>' +
          '</span></span>';
      } else {
        aside = '';
      }

      var tick = (step.kind !== 'review' && step.item)
        ? '<span class="id-stepc__tick" role="img" aria-label="' + escapeAttr(d.idStrDone || 'Step complete') + '">' + TICK + '</span>'
        : '';

      return '<a class="id-stepc" href="' + escapeAttr(step.url) + '" data-state="' + state + '"' +
        (state === 'current' ? ' aria-current="step"' : '') + '>' +
          '<span class="id-stepc__n">' + step.n + '</span>' +
          '<span class="id-stepc__body">' +
            '<span class="id-stepc__eyebrow">' + escapeHtml(step.eyebrow) + '</span>' +
            '<span class="id-stepc__label">' + escapeHtml(step.label) + '</span>' +
          '</span>' + aside + tick +
        '</a>';
    }).join('') + '</div>';
  }
  Builder.renderSteps = renderSteps;

  /* ------------------------------------------------------- the cards --- */
  /**
   * Cards show what the pairing would cost, the way the category expects:
   * a setting priced with the stone already chosen, and vice versa. A card stays
   * silent until the other half exists, so nothing implies a price the shopper
   * has not decided.
   */
  function decorateCards() {
    var cards = document.querySelectorAll('[data-id-card]');
    if (!cards.length) return;
    var cfg = config();
    var s = memory;

    cards.forEach(function (card) {
      var target = card.querySelector('[data-id-combined]');
      if (!target) return;

      var role = card.getAttribute('data-id-role') === 'diamond' ? 'diamond' : 'setting';
      var counterpart = role === 'setting' ? s.diamond : s.setting;
      var price = parseInt(card.getAttribute('data-id-price'), 10);

      if (!counterpart || typeof counterpart.price !== 'number' || isNaN(price)) {
        target.hidden = true;
        target.textContent = '';
        return;
      }

      var label = role === 'setting' ? cfg.strings.withStone : cfg.strings.withSetting;
      target.hidden = false;
      target.innerHTML = escapeHtml(label) + ' <b>' +
        escapeHtml(formatMoney(price + counterpart.price, cfg.moneyFormat)) + '</b>';
    });
  }
  Builder.decorateCards = decorateCards;

  /* -------------------------------------------------------- delegation --- */
  document.addEventListener('click', function (event) {
    var pick = event.target.closest('[data-id-builder-pick]');
    if (pick) {
      event.preventDefault();
      var kind = pick.getAttribute('data-id-builder-pick');
      var form = pick.closest('form') || document;
      var item = readItemFrom(pick, form);
      if (kind === 'setting' && pick.hasAttribute('data-id-require-size') && !item.size) {
        announce(config().strings.needSize);
        var sizeEl = form.querySelector('[data-id-size-input]');
        if (sizeEl) { sizeEl.focus(); sizeEl.reportValidity && sizeEl.reportValidity(); }
        return;
      }
      Builder.select(kind, item);
      var go = pick.getAttribute('data-id-next-url');
      if (go) window.location.href = go;
      return;
    }

    if (event.target.closest('[data-id-builder-clear]')) {
      event.preventDefault();
      Builder.clear();
      return;
    }

    var addBtn = event.target.closest('[data-id-builder-add]');
    if (addBtn) {
      event.preventDefault();
      addBtn.disabled = true;
      Builder.addToCart()
        .then(function () { window.location.href = config().cartUrl; })
        .catch(function (err) { addBtn.disabled = false; announce(err.message || config().strings.error); });
      return;
    }

    var removeBtn = event.target.closest('[data-id-builder-remove]');
    if (removeBtn) {
      event.preventDefault();
      Builder.remove(removeBtn.getAttribute('data-id-builder-remove'));
      return;
    }

    var opener = event.target.closest('[data-id-open]');
    if (opener) {
      var dlg = document.querySelector(opener.getAttribute('data-id-open'));
      if (dlg && dlg.showModal) { event.preventDefault(); dlg.showModal(); }
      return;
    }
    var closer = event.target.closest('[data-id-close]');
    if (closer) {
      var parent = closer.closest('dialog');
      if (parent) { event.preventDefault(); parent.close(); }
    }
  });

  function readItemFrom(el, form) {
    var variantId = el.getAttribute('data-id-variant-id');
    // A variant picker on the page wins over the button's static value.
    var live = form.querySelector && form.querySelector('[name="id"]');
    if (live && live.value) variantId = live.value;

    var sizeEl = form.querySelector && form.querySelector('[data-id-size-input]');
    return {
      variantId: parseInt(variantId, 10),
      productId: el.getAttribute('data-id-product-id') || null,
      title: el.getAttribute('data-id-title') || '',
      url: el.getAttribute('data-id-url') || '',
      image: el.getAttribute('data-id-image') || '',
      price: parseInt(el.getAttribute('data-id-price'), 10) || 0,
      shape: el.getAttribute('data-id-shape') || '',
      metal: el.getAttribute('data-id-metal') || '',
      size: sizeEl && sizeEl.value ? sizeEl.value : (el.getAttribute('data-id-size') || '')
    };
  }

  /* Keep the button data in sync when the shopper changes a variant. */
  document.addEventListener('change', function (event) {
    if (!event.target.matches('[name="id"], [data-id-size-input], .id-shapes__input, .id-metals__input')) return;
    var form = event.target.closest('form');
    if (!form) return;
    var btn = form.querySelector('[data-id-builder-pick]');
    if (!btn) return;
    var live = form.querySelector('[name="id"]');
    if (live && live.value) btn.setAttribute('data-id-variant-id', live.value);
  });

  /* ------------------------------------------------------------- a11y --- */
  function announce(message) {
    var region = document.getElementById('id-builder-status');
    if (!region) {
      region = document.createElement('p');
      region.id = 'id-builder-status';
      region.className = 'id-visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = message;
  }
  Builder.announce = announce;

  /* -------------------------------------------------------------- boot --- */
  function renderAll() { renderBar(); renderSteps(); decorateCards(); }

  document.addEventListener('icon-diamonds:builder:change', renderAll);
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { memory = readStore(); renderAll(); document.dispatchEvent(new CustomEvent('icon-diamonds:builder:sync')); }
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll);
  } else {
    renderAll();
  }
})();

/* ---------------------------------------------------------------- lightbox --
   Click sulla foto principale della scheda prodotto: apre l'immagine grande.
   Nessuna dipendenza, si auto-disattiva se la scheda non ha immagini.        */
(function () {
  var frame = document.querySelector('.id-media__frame');
  if (!frame) return;
  var box, boxImg;

  function biggest(img) {
    var src = img.currentSrc || img.src || '';
    if (!src) return '';
    try {
      var u = new URL(src, location.href);
      if (u.hostname.indexOf('shopify') > -1) { u.searchParams.set('width', '1800'); }
      return u.toString();
    } catch (e) { return src; }
  }

  function build() {
    box = document.createElement('div');
    box.className = 'id-lightbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    boxImg = document.createElement('img');
    boxImg.alt = '';
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'id-lightbox__close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '\u2715';
    box.appendChild(boxImg);
    box.appendChild(close);
    document.body.appendChild(box);
    box.addEventListener('click', hide);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') hide(); });
  }

  function show() {
    var img = frame.querySelector('img');
    if (!img) return;
    if (!box) build();
    boxImg.src = biggest(img);
    boxImg.alt = img.alt || '';
    box.setAttribute('data-open', '');
    document.documentElement.style.overflow = 'hidden';
  }

  function hide() {
    if (!box) return;
    box.removeAttribute('data-open');
    document.documentElement.style.overflow = '';
  }

  frame.addEventListener('click', function (e) {
    if (e.target && e.target.tagName === 'IMG') { e.preventDefault(); show(); }
  });
})();

/* --- id-hero-sizes: fa scegliere al browser la variante piu' grande del srcset
   per la foto principale, invece di una versione piccola e sgranata. --- */
(function(){
  var f=document.querySelector('.id-media__frame'); if(!f) return;
  function fix(){ var img=f.querySelector('img'); if(!img||!img.getAttribute('srcset')) return;
    var d=Math.min(window.devicePixelRatio||1,2); var w=Math.min(Math.ceil(f.clientWidth*d),1000); if(w>0) img.sizes=w+'px'; }
  fix(); window.addEventListener('resize',fix); window.addEventListener('load',fix);
  document.addEventListener('click',function(e){ if(e.target&&e.target.closest&&e.target.closest('.id-media__thumb')) setTimeout(fix,60); });
})();

/* ------------------------------------------------------------------ wishlist --
   Wishlist leggera, senza app: salva gli handle in localStorage.
   - cuore sulla foto della scheda prodotto (iniettato via JS, niente Liquid)
   - contatore accanto al link Wishlist nel menu
   - pagina /pages/wishlist con la griglia dei salvati                        */
(function () {
  var KEY = 'id_wishlist';

  function read() {
    try { var v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v : []; }
    catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    paintCount();
  }
  function has(h) { return read().indexOf(h) > -1; }
  function toggle(h) {
    var l = read(), i = l.indexOf(h);
    if (i > -1) l.splice(i, 1); else l.unshift(h);
    write(l);
    return i === -1;
  }

  function heartSVG(filled) {
    return '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="' +
      (filled ? 'currentColor' : 'none') +
      '" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M12 20.5s-7.5-4.6-7.5-9.7A4.3 4.3 0 0 1 12 7.7a4.3 4.3 0 0 1 7.5 3.1c0 5.1-7.5 9.7-7.5 9.7z"/></svg>';
  }

  function paintCount() {
    var n = read().length;
    document.querySelectorAll('[data-id-wishlist-count]').forEach(function (el) {
      el.textContent = n ? '(' + n + ')' : '';
    });
    document.querySelectorAll('a[href$="/pages/wishlist"]').forEach(function (a) {
      if (a.querySelector('[data-id-wishlist-count]')) return;
      var s = document.createElement('span');
      s.setAttribute('data-id-wishlist-count', '');
      s.style.marginInlineStart = '.35em';
      s.textContent = n ? '(' + n + ')' : '';
      a.appendChild(s);
    });
  }

  /* --- cuore sulla scheda prodotto --- */
  function currentHandle() {
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  function mountButton() {
    var host = document.querySelector('.id-media__main');
    var handle = currentHandle();
    if (!host || !handle || host.querySelector('.id-wish')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'id-wish';
    btn.setAttribute('data-handle', handle);
    function render() {
      var on = has(handle);
      btn.innerHTML = heartSVG(on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
      btn.title = on ? 'Saved to your wishlist' : 'Save to your wishlist';
      btn.classList.toggle('is-on', on);
    }
    btn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      toggle(handle); render();
    });
    render();
    host.appendChild(btn);
  }

  /* --- pagina wishlist --- */
  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var loc = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
    try { return new Intl.NumberFormat(loc, { style: 'currency', currency: cur }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(2); }
  }

  async function mountPage() {
    var root = document.querySelector('[data-id-wishlist-root]');
    if (!root) return;
    var list = read();
    if (!list.length) {
      root.innerHTML = '<p class="id-wish__empty">Your wishlist is empty. Tap the heart on any piece to save it here.</p>';
      return;
    }
    var cards = [];
    for (var i = 0; i < list.length; i++) {
      var h = list[i];
      try {
        var r = await fetch('/products/' + h + '.js');
        if (!r.ok) continue;
        var p = await r.json();
        var img = p.featured_image ? p.featured_image : (p.images && p.images[0]);
        cards.push(
          '<li class="id-wish__card" data-handle="' + h + '">' +
            '<a href="/products/' + h + '">' +
              (img ? '<span class="id-wish__thumb"><img src="' + img + '" alt="" loading="lazy"></span>' : '<span class="id-wish__thumb"></span>') +
              '<span class="id-wish__name">' + p.title + '</span>' +
            '</a>' +
            '<span class="id-wish__price">' + money(p.price) + '</span>' +
            '<button type="button" class="id-wish__remove" data-remove="' + h + '">Remove</button>' +
          '</li>'
        );
      } catch (e) {}
    }
    root.innerHTML = '<ul class="id-wish__grid">' + cards.join('') + '</ul>';
    root.querySelectorAll('[data-remove]').forEach(function (b) {
      b.addEventListener('click', function () {
        toggle(b.getAttribute('data-remove'));
        mountPage();
      });
    });
  }

  function init() { mountButton(); paintCount(); mountPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================ complete-ring ==
   Barra "Design your ring" + blocco "Complete this ring" con i diamanti reali
   e il totale dell'anello finito. Si appoggia a IconDiamondsBuilder.          */
(function () {
  var B = window.IconDiamondsBuilder;
  var pick = document.querySelector('[data-id-builder-pick]');
  if (!B || !pick) return;

  var KIND = pick.getAttribute('data-id-builder-pick'); // 'setting' | 'diamond'

  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var loc = (window.Shopify && window.Shopify.locale) || 'en';
    try { return new Intl.NumberFormat(loc, { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(0); }
  }

  function itemFromPick() {
    var d = pick.dataset;
    return {
      productId: d.idProductId, variantId: d.idVariantId, title: d.idTitle,
      price: parseInt(d.idPrice, 10) || 0, image: d.idImage, url: d.idUrl, shape: d.idShape
    };
  }

  function currentShape() {
    var el = document.querySelector('[data-id-shape-value]');
    var v = el ? el.textContent.trim() : (pick.dataset.idShape || '');
    return v;
  }

  /* ------------------------------------------------------------- step bar -- */
  function stepBar() {
    var anchor = document.querySelector('.id-pdp');
    if (!anchor || document.querySelector('.id-dyr')) return;
    var nav = document.createElement('nav');
    nav.className = 'id id-dyr page-width';
    nav.setAttribute('aria-label', 'Design your ring');
    anchor.parentNode.insertBefore(nav, anchor);
    paintSteps();
  }

  function paintSteps() {
    var nav = document.querySelector('.id-dyr');
    if (!nav) return;
    var s = B.get() || {};
    var setting = s.setting, diamond = s.diamond;
    var here = KIND;

    var rows = [
      {
        n: 1, label: 'Setting',
        value: setting ? setting.title : (here === 'setting' ? document.querySelector('.id-title') ? document.querySelector('.id-title').textContent.trim() : 'Choose a setting' : 'Choose a setting'),
        price: setting ? setting.price : (here === 'setting' ? itemFromPick().price : null),
        href: '/collections/nivoda-settings',
        done: !!setting, current: here === 'setting' && !setting
      },
      {
        n: 2, label: 'Diamond',
        value: diamond ? diamond.title : (here === 'diamond' ? (pick.dataset.idTitle || 'This diamond') : 'Choose your centre stone'),
        price: diamond ? diamond.price : (here === 'diamond' ? itemFromPick().price : null),
        href: '/collections/diamonds',
        done: !!diamond, current: here === 'diamond' || (!!setting && !diamond)
      },
      {
        n: 3, label: 'Your ring',
        value: (setting && diamond) ? 'Ready — ring size and checkout' : 'Ring size and checkout',
        price: (setting && diamond) ? B.totalCents() : null,
        href: '/pages/ring-builder',
        done: false, current: !!(setting && diamond)
      }
    ];

    nav.innerHTML =
      '<p class="id-dyr__eyebrow">Design your ring</p><ol class="id-dyr__list">' +
      rows.map(function (r) {
        return '<li class="id-dyr__item' + (r.current ? ' is-current' : '') + (r.done ? ' is-done' : '') + '">' +
          '<a href="' + r.href + '">' +
            '<span class="id-dyr__n">' + (r.done ? '&#10003;' : r.n) + '</span>' +
            '<span class="id-dyr__body">' +
              '<span class="id-dyr__label">' + r.label + '</span>' +
              '<span class="id-dyr__value">' + r.value + (r.price ? ' &middot; ' + money(r.price) : '') + '</span>' +
            '</span>' +
          '</a></li>';
      }).join('') + '</ol>';
  }

  /* --------------------------------------------- complete this ring block -- */
  var stones = null;

  function parseStone(p) {
    var t = p.title;
    var ct = parseFloat((t.match(/([\d.]+)\s*ct/i) || [])[1]);
    var shape = ((t.match(/ct\s+([A-Za-z ]+?)\s+(Natural|Lab)/i) || [])[1] || '').trim();
    var col = (t.match(/Colou?r\s+([A-Z])/) || [])[1] || '';
    var cl = (t.match(/Clarity\s+([A-Z0-9]+)/) || [])[1] || '';
    var lab = /IGI/.test(t) ? 'IGI' : (/GIA/.test(t) ? 'GIA' : '');
    var v = p.variants && p.variants[0];
    return {
      handle: p.handle, shape: shape, ct: ct, col: col, cl: cl, lab: lab,
      grown: /Lab/i.test(t) ? 'Lab grown' : 'Natural',
      price: Math.round(parseFloat(v ? v.price : 0) * 100),
      item: {
        productId: String(p.id), variantId: String(v ? v.id : ''), title: p.title,
        price: Math.round(parseFloat(v ? v.price : 0) * 100),
        image: p.images && p.images[0] ? p.images[0].src : '', url: '/products/' + p.handle, shape: shape
      }
    };
  }

  // id-shape-pool v1 — carica le pietre dalla collezione della forma corrente.
  // Prima si leggeva /collections/diamonds con limit 250: su 3.396 prodotti quei primi 250
  // contenevano solo Asscher, Heart, Princess e Radiant, quindi per ogni altra forma il
  // blocco rispondeva 'no stone in stock'. Cache per forma.
  var ID_SHAPE_COLLECTIONS = {
    round: 'round-diamonds',
    oval: 'oval-diamonds',
    emerald: 'emerald-cut-diamonds',
    cushion: 'cushion-cut-diamonds',
    pear: 'pear-shaped-diamonds',
    marquise: 'marquise-diamonds',
    princess: 'princess-cut-diamonds',
    radiant: 'radiant-cut-diamonds',
    heart: 'heart-shaped-diamonds',
    asscher: 'asscher-cut-diamonds'
  };
  function idShapeCollection(){
    try {
      var k = String(currentShape() || '').trim().split(/[\s-]+/)[0].toLowerCase();
      return ID_SHAPE_COLLECTIONS[k] || 'diamonds';
    } catch (e) { return 'diamonds'; }
  }
  function idShapeLabel(){
    try {
      var s = String(currentShape() || '').trim();
      return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() + ' ' : '';
    } catch (e) { return ''; }
  }
  var idStonePools = {};

  async function loadStones(){
    var key = 'all';
    try {
      var sh = currentShape();
      if (sh) { key = String(sh).trim().split(/[\s-]+/)[0].toLowerCase(); }
    } catch (e) {}
    if (idStonePools[key]) return idStonePools[key];
    var handle = ID_SHAPE_COLLECTIONS[key] || 'diamonds';
    var out = [];
    try {
      var r = await fetch('/collections/' + handle + '/products.json?limit=250');
      var j = await r.json();
      out = (j.products || []).filter(function (p) { return (p.variants || []).some(function (x) { return x.available; }); }).map(parseStone).filter(function(s){ return s && s.price; });
    } catch (e) { out = []; }
    idStonePools[key] = out;
    return out;
  }

  function threeOf(list) {
    /* sotto mezzo carato non e' una pietra da anello di fidanzamento */
    var bridal = list.filter(function (x) { return x.ct >= 0.5; });
    var pool = bridal.length >= 3 ? bridal : list;
    if (pool.length <= 3) return pool.slice().sort(function (a, b) { return a.price - b.price; });
    var s = pool.slice().sort(function (a, b) { return a.price - b.price; });
    return [s[0], s[Math.floor(s.length / 2)], s[s.length - 1]];
  }

  function stoneLine(s) {
    return [s.ct ? s.ct.toFixed(2) + ' ct' : null, s.shape, s.col ? 'colour ' + s.col : null, s.cl, s.lab].filter(Boolean).join(' &middot; ');
  }

  async function completeBlock() {
    if (KIND !== 'setting') return;
    var host = document.querySelector('.id-trust');
    if (!host || document.querySelector('.id-complete')) return;

    var box = document.createElement('section');
    box.className = 'id-complete';
    host.parentNode.insertBefore(box, host.nextSibling);
    await paintComplete();
  }

  async function paintComplete() {
    var box = document.querySelector('.id-complete');
    if (!box) return;
    var all = await loadStones();
    var shape = currentShape();
    var mine = all.filter(function (s) { return s.shape && shape && s.shape.toLowerCase() === shape.toLowerCase(); });
    var settingItem = itemFromPick();

    if (!mine.length) {
      box.innerHTML =
        '<h2 class="id-complete__h">Complete this ring</h2>' +
        '<p class="id-complete__note">We have no ' + (shape || 'matching') + ' stone in stock right now. ' +
        '<a href="/pages/diamonds">Search our full certified inventory</a> &mdash; over 500,000 natural and lab grown diamonds, each with its lab report.</p>';
      return;
    }

    var three = threeOf(mine);
    box.innerHTML =
      '<h2 class="id-complete__h">Complete this ring</h2>' +
      '<p class="id-complete__note">Setting ' + money(settingItem.price) + '. Pick a centre stone and see the finished price.</p>' +
      '<ul class="id-complete__list">' +
      three.map(function (s, i) {
        return '<li class="id-complete__row">' +
          '<span class="id-complete__stone">' +
            '<span class="id-complete__name">' + (['Essential', 'Our pick', 'Statement'][i] || '') + '</span>' +
            '<span class="id-complete__spec">' + stoneLine(s) + '</span>' +
          '</span>' +
          '<span class="id-complete__total"><span class="id-complete__tl">Ring total</span>' + money(settingItem.price + s.price) + '</span>' +
          '<button type="button" class="id-btn id-btn--ghost id-complete__pick" data-stone="' + s.handle + '">Choose</button>' +
        '</li>';
      }).join('') +
      '</ul>' +
      '<p class="id-complete__more"><a href="/collections/' + idShapeCollection() + '">See all ' + idShapeLabel() + 'diamonds in stock</a> &middot; <a href="/pages/diamonds">or search 500,000+ certified diamonds</a></p>' +
      '<div class="id-complete__done" hidden></div>';

    box.querySelectorAll('[data-stone]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var s = (stones || []).find(function (x) { return x.handle === btn.getAttribute('data-stone'); });
        if (!s) return;
        B.start();
        B.select('setting', itemFromPick());
        B.select('diamond', s.item);
        paintSteps();
        var done = box.querySelector('.id-complete__done');
        done.hidden = false;
        done.innerHTML =
          '<p class="id-complete__ready"><strong>' + s.ct.toFixed(2) + ' ct ' + s.shape + '</strong> paired with this setting &mdash; ' +
          '<strong>' + money(B.totalCents()) + '</strong></p>' +
          '<button type="button" class="id-btn id-complete__add">Add the complete ring to cart</button>' +
          '<p class="id-complete__hint"></p>';
        done.querySelector('.id-complete__add').addEventListener('click', async function () {
          var size = document.querySelector('[name="properties[Ring size]"], [data-id-ring-size]');
          if (size && !size.value) {
            done.querySelector('.id-complete__hint').textContent = 'Choose a ring size first — the first resize is free within 60 days.';
            size.scrollIntoView({ block: 'center', behavior: 'smooth' });
            try { size.focus(); } catch (e) {}
            return;
          }
          done.querySelector('.id-complete__hint').textContent = '';
          try { await B.addToCart(); } catch (e) {}
        });
        done.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    });
  }

  /* la forma del centro cambia -> ricalcola le proposte */
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.id-shapes')) {
      setTimeout(function () { paintComplete(); }, 120);
    }
  });

  function init() { stepBar(); completeBlock(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();


/* id-setting-cta v1 - the pick was silently blocked when no ring size was chosen,
   and the CTA kept sending people back to the diamonds even when a stone was
   already selected. Additive: nothing above this line is changed. */
(function () {
  function sizeSelect() {
    return document.querySelector('select[name="properties[Ring size]"]');
  }
  function state() {
    try { return window.IconDiamondsBuilder ? window.IconDiamondsBuilder.get() : null; }
    catch (e) { return null; }
  }
  function hint(btn, text) {
    var el = btn.parentNode ? btn.parentNode.querySelector(".id-pickhint") : null;
    if (!el) {
      el = document.createElement("p");
      el.className = "id-pickhint";
      if (btn.parentNode) { btn.parentNode.insertBefore(el, btn.nextSibling); }
    }
    el.textContent = text;
    var sel = sizeSelect();
    if (sel) {
      sel.classList.add("id-select--flag");
      try { sel.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (e2) { sel.scrollIntoView(); }
      try { sel.focus({ preventScroll: true }); } catch (e3) {}
    }
  }
  function clearHint() {
    var el = document.querySelector(".id-pickhint");
    if (el && el.parentNode) { el.parentNode.removeChild(el); }
    var sel = sizeSelect();
    if (sel) { sel.classList.remove("id-select--flag"); }
  }
  function relabel() {
    var btn = document.querySelector('[data-id-builder-pick="setting"]');
    if (!btn) { return; }
    var st = state();
    if (!st || !st.diamond) { return; }
    btn.setAttribute("data-id-next-url", "/pages/ring-builder");
    if (btn.getAttribute("data-id-relabelled")) { return; }
    btn.setAttribute("data-id-relabelled", "1");
    btn.textContent = "Set my diamond in this ring";
  }
  document.addEventListener("click", function (e) {
    var btn = e.target && e.target.closest ? e.target.closest("[data-id-builder-pick]") : null;
    if (!btn) { return; }
    var kind = btn.getAttribute("data-id-builder-pick");
    if (!kind) { return; }
    setTimeout(function () {
      var st = state();
      if (st && st[kind]) { clearHint(); return; }
      var sel = sizeSelect();
      if (sel && !sel.value) { hint(btn, "Choose a ring size first, then this setting can be added to your ring."); }
    }, 80);
  }, true);
  document.addEventListener("change", function (e) {
    if (e.target && e.target.name === "properties[Ring size]" && e.target.value) { clearHint(); }
  });
  function boot() { relabel(); }
  if (document.readyState === "loading") { document.addEventListener("DOMContentLoaded", boot); } else { boot(); }
  setTimeout(boot, 900);
})();
